import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import decode from 'jwt-decode';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import IsJsonString from '../helpers/utils/jsonParser';
import { User } from '../models/user';

export interface IAC_controls {
  role: string,
  urls: string[],
  create_action: boolean,
  read_action: boolean,
  update_action: boolean,
  delete_action: boolean,
  dismiss_notification: boolean,
  empty?: boolean,
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private base_url = '';

  private currentUserSubject: BehaviorSubject<any>;
  public currentUser: Observable<any>;
  private token: any;

  private accessControls$: BehaviorSubject<any>;

  constructor(private _router: Router, private http: HttpClient, private cookieService: CookieService) {

    const checkIfJsonString = IsJsonString(localStorage.getItem('currentUser'))
    const localStorageData = (localStorage.getItem('currentUser') != null && checkIfJsonString)
      ? JSON.parse(localStorage.getItem('currentUser'))
      : localStorage.clear()

    this.base_url = environment.base_url;
    this.currentUserSubject = new BehaviorSubject<any>(localStorageData);
    this.accessControls$ = new BehaviorSubject<any>(null)
    this.currentUser = this.currentUserSubject.asObservable();
  }

  private access_controls: IAC_controls = {
    role: '',
    urls: [],
    create_action: false,
    read_action: false,
    update_action: false,
    delete_action: false,
    dismiss_notification: false,
  };

  private _resetAC() {
    this.access_controls.role = ''
    this.access_controls.urls = []
    this.access_controls.create_action = false
    this.access_controls.read_action = false
    this.access_controls.update_action = false
    this.access_controls.delete_action = false
    this.access_controls.dismiss_notification = false
  }

  private createAccessControls = (role: string) => {
    const urls = [
      "dashboard",
      "sensors",
      "map",
      "policing",
      "groups",
      "users-management/all-users",
      "all-notifications",
      "rules",
      "account",
      "profile",
      "logout",
      "booking",
    ]

    let rights: IAC_controls = { ...this.access_controls }
    switch (role) {
      case "user":
        rights.role = role
        rights.read_action = true;
        rights.urls = [
          "dashboard",
          "sensors",
          "map",
          "groups",
          "all-notifications",
          "logout",
          "booking"
        ];
        return this.access_controls = rights
      case "admin":
        rights.role = role
        rights.read_action = true;
        rights.delete_action = true;
        rights.update_action = true;
        rights.create_action = true;
        rights.dismiss_notification = true;
        rights.urls = urls;

        return this.access_controls = rights
      case "customer_admin":
        rights.role = role
        rights.read_action = true;
        rights.delete_action = true;
        rights.update_action = true;
        rights.create_action = true;
        rights.dismiss_notification = true;
        rights.urls = urls;

        return this.access_controls = rights
      case "municipality_police":
        rights.role = role
        rights.read_action = true;
        rights.dismiss_notification = true;
        rights.urls = [
          "dashboard",
          "sensors",
          "map",
          "groups",
          "all-notifications",
          "logout"
        ]

        return this.access_controls = rights
      default:
        return this.access_controls = rights
    }
  }

  /**
   * Gets user login status and access_controls
   * if false resetAC()
   */
  public get userLoginStatusAndAC(): boolean {
    let condition: boolean = !this.isLoggedIn && (this.access_controls['role'] !== '' && this.access_controls['urls'].length > 0);
    return condition;
  }

  public resetAC() {
    return this._resetAC()
  }

  public get currentUserValue(): any {
    return this.currentUserSubject.value;
  }

  /**
   * Get the currentUser role
   */
  public getCurrentUserRole(): string {
    return this.currentUserSubject.value.role;
  }

  public getUserToken(): string {
    if (this.currentUserSubject.value !== null) {
      return this.currentUserSubject.value.token
    } else {
      if (localStorage.getItem('currentUser') !== null) {
        const token = localStorage.getItem('currentUser')
        return token['token']
      } else return null
    }
  }

  /**
    * Check if user is already logged in
   */
  public get isLoggedIn(): boolean {
    const userLoggedin = localStorage.getItem('currentUser')
    console.log(`Auth Service userLoggedin : `, userLoggedin)
    return (userLoggedin !== null)
  }

  /**
    * Check if user is logged in and is verified as Role : USER
   */
  public get isLoggedInSimpleUser(): boolean {
    const userLoggedinSimpleUser = JSON.parse(localStorage.getItem('currentUser'))
    return (userLoggedinSimpleUser !== null && userLoggedinSimpleUser.role === 'user')
  }

  /**
   * Check if user is logged in and is verified as Role : CUSTOMER_ADMIN
   */
  public get isLoggedInCustomerAdmin(): boolean {
    const userLoggedInCustAdmin = JSON.parse(localStorage.getItem('currentUser'))
    return (userLoggedInCustAdmin !== null && userLoggedInCustAdmin.role === 'customer_admin')
  }

  /**
   * Check if user is logged in and is verified as Role : MUNICIPAL_POLICE
   */
  public get isLoggedInMunicipalPolice(): boolean {
    // let isNotMunicipalityPolice = JSON.parse(localStorage.getItem('currentUser'))['role'] !== 'municipality_police'
    // return localStorage.getItem('currentUser') && localStorage.getItem('currentUser') !== null && isNotMunicipalityPolice
    const userLoggedInMunPolice = JSON.parse(localStorage.getItem('currentUser'))
    return (userLoggedInMunPolice !== null && userLoggedInMunPolice.role === 'municipality_police')
  }

  /**
   * Check if user is logged in and is verified as Role : ADMIN
   */
  public get isLoggedInAndAdmin(): boolean {
    const user = JSON.parse(localStorage.getItem('currentUser'))
    return (user !== null && user.role === 'admin')
  }

  public get getAC_controls() {
    return this.accessControls$.asObservable()
  }

  /**
   * this is used to clear anything that needs to be removed
   */
  clear(): void {
    localStorage.removeItem('currentUser');
    localStorage.clear();
    this.currentUserSubject.next(null);
    this._router.navigate(['/login']);
  }

  /**
   * check for expiration and if token is still existing or not
   * @return {boolean}
   */
  isAuthenticated(): boolean {
    return localStorage.getItem('token') != null && !this.isTokenExpired();
  }

  // simulate jwt token is valid
  // https://github.com/theo4u/angular4-auth/blob/master/src/app/helpers/jwt-helper.ts
  isTokenExpired(): boolean {
    return false;
  }

  login(loginObject: User) {
    return this.http.post<any>(`${this.base_url}/authenticate`, loginObject)
      .pipe(
        map(user => {
          // login successful if there's a jwt token in the response
          // when jwt implementation is ready put in the if expression (user && user.token)
          if (user) {
            // store user details and jwt token in local storage to keep user logged in between page refreshes
            localStorage.setItem('currentUser', JSON.stringify(user));
            this.createAccessControls(user.role)
            this.accessControls$.next(this.access_controls)
            if (user.role !== 'driver') {
              this.currentUserSubject.next(user)
              return user;
            }
            else {
              throwError('This role is not applicable for this application')
            }
          }
        }),
        catchError(err => {
          if (err['error']['non_field_errors']) {
            let errors = [...err['error']['non_field_errors']]
            return throwError(errors);
          } else return throwError(err)
        })
      )
  }

  /**
   * this is used to clear local storage and also the route to login
   */
  logout(): void {
    this._resetAC()
    this.clear();
  }

  decode() {
    return decode(localStorage.getItem('token'));
  }
}
